import { AllTodosDocument, useSetOutreachScheduleMutation } from 'graphql/bindings/AdminBindings'
import {
  Box,
  FormModal,
  SpinnerButton,
  TextField,
  Typography,
  ValueAutocompleteField,
} from '@forgeglobal/design-framework'
import { Field, Form, FormRenderProps } from 'react-final-form'
import { MutationResult } from 'react-apollo'
import { OutreachOptions, outreachOptionsDropdown } from 'util/Enums'
import React from 'react'
import moment, { Duration, Moment } from 'moment'

type FormShape = {
  days?: number
  outreachType: { label: string; value: OutreachOptions }
  lastContacted: Moment
  mutationResult: MutationResult
}

export const getNextOutreachString = (values: FormShape) => {
  const outreachType = values.outreachType?.value

  if (typeof outreachType === 'undefined') return ''

  let schedule

  if (outreachType === OutreachOptions.TwoWeeks) {
    schedule = moment.duration(14, 'days')
  } else if (outreachType === OutreachOptions.OneWeek) {
    schedule = moment.duration(7, 'days')
  } else if (outreachType === OutreachOptions.Never) {
    return 'We won\'t remind you to contact this account'
  } else {
    schedule = moment.duration(parseInt(values.days ? values.days.toString() : '0'), 'days')
  }

  const numbDays = schedule.asDays()
  const nextOutreach = values.lastContacted
    .clone()
    .add(schedule)
    .format('MMMM Do, YYYY')

  return `You'll be reminded to contact this account once every ${numbDays} days starting on ${nextOutreach}`
}

export const OutreachScheduleForm: React.FC<FormRenderProps<FormShape>> = ({ handleSubmit, values, valid }) => {
  const mutateInProgress = values.mutationResult.called
  const nextOutreachString = getNextOutreachString(values)
  return (
    <form onSubmit={handleSubmit}>
      <ValueAutocompleteField
        name="outreachType"
        label="Remind me if this account has not been contacted in"
        options={outreachOptionsDropdown}
      />
      {values.outreachType.value === OutreachOptions.Custom && (
        <Field name="days" label="Number of days" component={TextField} fullWidth />
      )}
      <Box pt={1} pl={2}>
        <Typography variant="caption" color="textSecondary">
          {nextOutreachString}
        </Typography>
      </Box>
      <Box mt={2}>
        <SpinnerButton
          variant="contained"
          color="primary"
          type="submit"
          fullWidth
          disabled={!valid}
          loading={mutateInProgress}
          caption="Save"
        />
      </Box>
    </form>
  )
}

interface OutreachScheduleModalProps {
  hideModal: () => void
  accountId: string
  currentSchedule?: Duration
  lastContacted: Moment
}

export const durationToOutreachOptions = (duration?: Duration) => {
  if (!duration) return OutreachOptions.Never
  else if (duration.asDays() === 7) return OutreachOptions.OneWeek
  else if (duration.asDays() === 14) return OutreachOptions.TwoWeeks
  return OutreachOptions.Custom
}

export const OutreachScheduleModal: React.FC<OutreachScheduleModalProps> = ({
  accountId,
  currentSchedule,
  hideModal,
  lastContacted,
}) => {
  const [mutate, mutationResult] = useSetOutreachScheduleMutation({
    refetchQueries: [{ query: AllTodosDocument }],
    onCompleted: hideModal,
  })
  const onSubmit = (values: FormShape) => {
    let schedule
    if (values.outreachType.value === OutreachOptions.TwoWeeks) {
      schedule = moment.duration(14, 'days').toISOString()
    } else if (values.outreachType.value === OutreachOptions.OneWeek) {
      schedule = moment.duration(7, 'days').toISOString()
    } else if (values.outreachType.value === OutreachOptions.Never) {
      schedule = null
    } else {
      schedule = moment.duration(parseInt(`${values.days}` || '0'), 'days').toISOString()
    }
    mutate({
      variables: {
        accountId: accountId,
        schedule: schedule,
      },
    })
  }
  const outreachType = durationToOutreachOptions(currentSchedule)
  const outreachTypeOption = outreachOptionsDropdown.find(o => o.value === outreachType)
  const initialValues: FormShape = {
    mutationResult,
    outreachType: outreachTypeOption!,
    days: currentSchedule && currentSchedule.asDays(),
    lastContacted,
  }

  return (
    <FormModal open hideModal={hideModal} title="Manage Suggested Outreach Schedule">
      <Form component={OutreachScheduleForm} onSubmit={onSubmit} initialValues={initialValues} />
    </FormModal>
  )
}
